import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wrench } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/"} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) navigate(res.user?.role === "admin" ? "/admin" : "/");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-b from-slate-50 to-blue-50">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <div className="h-11 w-11 rounded-xl bg-blue-600 flex items-center justify-center">
            <Wrench className="w-6 h-6 text-white" />
          </div>
          <span className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>PlumberHQ</span>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
          <h1 className="text-2xl font-bold mb-1.5">Welcome back</h1>
          <p className="text-slate-500 text-sm mb-6">Log in to your plumbing dashboard.</p>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-sm font-semibold">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 mt-1.5 text-base" data-testid="login-email" />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-semibold">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 mt-1.5 text-base" data-testid="login-password" />
            </div>
            {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={submitting} className="w-full h-12 text-base font-semibold bg-blue-600 hover:bg-blue-700" data-testid="login-submit">
              {submitting ? "Logging in…" : "Log in"}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm text-slate-500">
            New to PlumberHQ?{" "}
            <Link to="/signup" className="font-semibold text-blue-600 hover:underline" data-testid="login-to-signup">Create your company</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
